import { fetchAuthSession } from 'aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';
import { WEBSOCKET_AGENTS_URL } from '../env';

/**
 * Cliente del API WebSocket de agentes (whisperbi-agent-management-strands,
 * AgentsWebSocketApi). A diferencia de POST /agent/invoke, no tiene el
 * límite de 29 s de API Gateway: la pregunta se manda por la conexión, el
 * backend responde "accepted" al instante y después empuja mensajes de
 * progreso y el resultado final.
 *
 * Una sola conexión compartida (singleton). Se abre bajo demanda y, si se
 * cerró (inactividad de 10 min, red, token vencido), la siguiente pregunta
 * abre otra con un ID token fresco -- API Gateway solo valida el token en
 * el $connect (ws-gateway/authorizer.py), así que no hace falta más.
 *
 * Mensajes del servidor: {type: "accepted" | "progress" | "completed" |
 * "error", request_id, ...}. Después de "completed" con chart_pending, llega
 * {type: "chart", invocation_id, status, queryResults?, chart?, error?}: la
 * gráfica de ese turno, generada en el servidor (ver waitForChart).
 */

const CONNECT_TIMEOUT_MS = 10000;
// Por encima del Timeout de AgentRuntimeFunction (300 s): si no llegó nada
// para entonces, la respuesta ya no va a llegar.
const REQUEST_TIMEOUT_MS = 5 * 60 * 1000 + 30000;

// Latido mientras hay preguntas en curso: mantiene tráfico del navegador al
// servidor para que ningún intermediario de red corte la conexión por
// "inactiva" durante un turno largo (ws-gateway/router.py lo acepta sin
// responder). No evita que el teléfono suspenda la página: eso lo cubre la
// recuperación de la respuesta en InvokeAgentAPI.js.
const HEARTBEAT_INTERVAL_MS = 25000;
// Por encima del Timeout de ChartDataFunction (120 s).
const CHART_WAIT_TIMEOUT_MS = 150000;

let socket = null;
let connecting = null;
let heartbeat = null;
const pending = new Map(); // request_id -> { resolve, onProgress, invocationId, timer }
// Gráficas por llegar: invocation_id -> { resolve, timer }. Una gráfica que
// llega antes de que alguien la espere queda en earlyCharts.
const chartWaiters = new Map();
const earlyCharts = new Map();

const hasWork = () => pending.size > 0 || chartWaiters.size > 0;

const stopHeartbeat = () => {
    if (heartbeat) {
        clearInterval(heartbeat);
        heartbeat = null;
    }
};

const startHeartbeat = () => {
    if (heartbeat) return;
    heartbeat = setInterval(() => {
        if (!hasWork()) {
            stopHeartbeat();
            return;
        }
        if (socket && socket.readyState === WebSocket.OPEN) {
            try {
                socket.send(JSON.stringify({ action: 'ping' }));
            } catch (error) {
                console.warn('AgentSocket: no se pudo enviar el latido', error);
            }
        }
    }, HEARTBEAT_INTERVAL_MS);
};

export const isAgentSocketConfigured = () => Boolean(WEBSOCKET_AGENTS_URL);

const settle = (requestId, result) => {
    const entry = pending.get(requestId);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.delete(requestId);
    if (!hasWork()) stopHeartbeat();
    entry.resolve(result);
};

const settleChart = (invocationId, message) => {
    const waiter = chartWaiters.get(invocationId);
    if (!waiter) return false;
    clearTimeout(waiter.timer);
    chartWaiters.delete(invocationId);
    if (!hasWork()) stopHeartbeat();
    waiter.resolve(message);
    return true;
};

const failAllPending = (errorCode, message) => {
    for (const [requestId, entry] of pending) {
        settle(requestId, { success: false, error: message, errorCode, invocation_id: entry.invocationId });
    }
    // Las gráficas en espera se recuperan por HTTP (InvokeAgentAPI.awaitTurnChart).
    for (const invocationId of [...chartWaiters.keys()]) {
        settleChart(invocationId, null);
    }
};

const handleMessage = (event) => {
    let msg;
    try {
        msg = JSON.parse(event.data);
    } catch {
        console.warn('AgentSocket: mensaje no JSON ignorado', event.data);
        return;
    }
    // La gráfica llega cuando la pregunta ya se resolvió: se busca por
    // invocation_id, no entre las preguntas en curso.
    if (msg.type === 'chart') {
        if (msg.invocation_id && !settleChart(msg.invocation_id, msg)) {
            earlyCharts.set(msg.invocation_id, msg);
        }
        return;
    }
    const entry = pending.get(msg.request_id);
    if (!entry) return;

    if (msg.type === 'accepted') {
        entry.invocationId = msg.invocation_id;
        entry.onAccepted?.(msg.invocation_id);
    } else if (msg.type === 'progress') {
        entry.onProgress?.(msg);
    } else if (msg.type === 'completed') {
        settle(msg.request_id, { success: true, data: msg });
    } else if (msg.type === 'error') {
        // Mismo shape que devuelve invokeAgent() por HTTP: el backend manda
        // {error: <code>, message?: <texto legible>}.
        settle(msg.request_id, {
            success: false,
            error: msg.message || msg.error || 'Error al invocar el agente',
            errorCode: msg.error,
            // Motivo del rechazo de créditos y espera sugerida (utils/questionError.js).
            errorReason: msg.reason,
            retryAfterSeconds: msg.retry_after_seconds,
            invocation_id: msg.invocation_id || entry.invocationId,
        });
    }
};

export const connectAgentSocket = async () => {
    if (socket && socket.readyState === WebSocket.OPEN) return socket;
    if (connecting) return connecting;

    connecting = (async () => {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) throw new Error('No authentication token available');

        return new Promise((resolve, reject) => {
            const ws = new WebSocket(`${WEBSOCKET_AGENTS_URL}?token=${encodeURIComponent(token)}`);
            const timer = setTimeout(() => {
                ws.close();
                reject(new Error('WebSocket connect timeout'));
            }, CONNECT_TIMEOUT_MS);

            ws.onopen = () => {
                clearTimeout(timer);
                socket = ws;
                resolve(ws);
            };
            ws.onmessage = handleMessage;
            ws.onclose = (event) => {
                clearTimeout(timer);
                // Diagnóstico de cortes (p. ej. el teléfono suspende la página
                // al cambiar de app): el código y si la página estaba visible
                // dicen quién cerró. API Gateway registra lo mismo del lado del
                // servidor (disconnectStatusCode/disconnectReason).
                console.warn('AgentSocket: conexión cerrada', {
                    code: event?.code,
                    reason: event?.reason,
                    wasClean: event?.wasClean,
                    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
                    pendingRequests: pending.size
                });
                if (socket === ws) socket = null;
                // Si había preguntas en vuelo, el turno puede haber terminado
                // igual del lado del servidor (queda en la sesión/auditoría).
                failAllPending('connection_lost', 'Se perdió la conexión con el agente.');
                reject(new Error('WebSocket closed'));
            };
        });
    })();

    try {
        return await connecting;
    } finally {
        connecting = null;
    }
};

/**
 * Envía una pregunta al agente por WebSocket.
 * @param {Object} payload - mismo body que POST /agent/invoke
 * @param {Object} [options]
 * @param {Function} [options.onProgress] - recibe {stage, label, tool?}
 * @param {Function} [options.onAccepted] - recibe el invocation_id cuando el servidor acepta
 *   la pregunta (para poder recuperar la respuesta si la página se recarga)
 * @param {number} [options.timeoutMs] - espera máxima (por defecto REQUEST_TIMEOUT_MS;
 *   el Análisis Profundo usa más, ver InvokeAgentAPI.js)
 * @returns {Promise<{success: boolean, data?: Object, error?: string, errorCode?: string, invocation_id?: string}>}
 */
export const askAgent = async (payload, { onProgress, onAccepted, timeoutMs = REQUEST_TIMEOUT_MS } = {}) => {
    const ws = await connectAgentSocket();
    const requestId = uuidv4();

    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            settle(requestId, {
                success: false,
                error: 'El agente tardó demasiado en responder.',
                errorCode: 'timeout',
                invocation_id: pending.get(requestId)?.invocationId,
            });
        }, timeoutMs);

        pending.set(requestId, { resolve, onProgress, onAccepted, invocationId: null, timer });
        ws.send(JSON.stringify({ action: 'ask', request_id: requestId, ...payload }));
        startHeartbeat();
    });
};

/**
 * Espera la gráfica de un turno (mensaje "chart") por la conexión actual.
 * Resuelve con el mensaje, o null si la conexión se cerró o no llegó a
 * tiempo -- en ese caso el servidor la guardó y se recupera por HTTP.
 * @param {string} invocationId
 * @param {Object} [options]
 * @param {number} [options.timeoutMs]
 * @returns {Promise<Object|null>}
 */
export const waitForChart = (invocationId, { timeoutMs = CHART_WAIT_TIMEOUT_MS } = {}) => {
    if (earlyCharts.has(invocationId)) {
        const message = earlyCharts.get(invocationId);
        earlyCharts.delete(invocationId);
        return Promise.resolve(message);
    }
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return Promise.resolve(null);
    }
    return new Promise((resolve) => {
        const timer = setTimeout(() => settleChart(invocationId, null), timeoutMs);
        chartWaiters.set(invocationId, { resolve, timer });
        startHeartbeat();
    });
};
